import { useState } from 'react';
import {
  Archive,
  ArchiveRestore,
  ChevronDown,
  ChevronUp,
  FolderTree,
  Merge,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast';
import { ConfirmDestructiveDialog } from '@/components/confirm-destructive-dialog';
import { EmptyState, ErrorState } from '@/components/states';
import { CategoryFormDialog } from '@/features/categories/category-form-dialog';
import { MergeCategoryDialog } from '@/features/categories/merge-category-dialog';
import { useCategoriesQuery, useDeleteCategory, useUpdateCategory } from '@/hooks/use-categories';
import { describeError } from '@/lib/api-client';
import { pluralize } from '@/lib/format';
import type { Category } from '@/types/api';

/**
 * Category ordering is server-side (`sortOrder`). Moving an item swaps its sortOrder with its
 * neighbour, which is two PATCHes and keeps the API contract exactly as documented.
 */
export function CategoriesPage() {
  const categories = useCategoriesQuery();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const { toast } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Category | undefined>(undefined);
  const [merging, setMerging] = useState<Category | undefined>(undefined);
  const [deleting, setDeleting] = useState<Category | undefined>(undefined);

  const items = categories.data ?? [];

  const move = (index: number, direction: -1 | 1) => {
    const current = items[index];
    const neighbour = items[index + direction];
    if (!current || !neighbour) return;

    updateCategory.mutate(
      { categoryId: current.categoryId, body: { sortOrder: neighbour.sortOrder } },
      {
        onSuccess: () =>
          updateCategory.mutate(
            { categoryId: neighbour.categoryId, body: { sortOrder: current.sortOrder } },
            {
              onError: (error) =>
                toast({
                  title: 'Could not finish reordering',
                  description: describeError(error),
                  variant: 'error',
                }),
            },
          ),
        onError: (error) =>
          toast({
            title: 'Could not reorder categories',
            description: describeError(error),
            variant: 'error',
          }),
      },
    );
  };

  const toggleArchived = (category: Category) => {
    updateCategory.mutate(
      { categoryId: category.categoryId, body: { archived: !category.archived } },
      {
        onSuccess: () =>
          toast({
            title: category.archived ? 'Category restored' : 'Category archived',
            description: category.name,
            variant: 'success',
          }),
        onError: (error) =>
          toast({
            title: 'Could not change the archive state',
            description: describeError(error),
            variant: 'error',
          }),
      },
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">Categories</h1>
          <p className="text-sm text-muted-foreground">
            Categories are data: rename, reorder, archive or merge them without a deploy.
          </p>
        </div>
        <Button type="button" onClick={() => setCreateOpen(true)}>
          <Plus aria-hidden="true" className="h-4 w-4" />
          New category
        </Button>
      </header>

      <p role="status" aria-live="polite" className="sr-only">
        {categories.isPending
          ? 'Loading categories'
          : `${items.length} ${pluralize(items.length, 'category', 'categories')} loaded.`}
      </p>

      {categories.isError ? (
        <ErrorState
          title="Could not load categories"
          error={categories.error}
          onRetry={() => void categories.refetch()}
        />
      ) : categories.isPending ? (
        <ul className="flex flex-col gap-2" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((index) => (
            <li key={index}>
              <Skeleton className="h-20 w-full" />
            </li>
          ))}
        </ul>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<FolderTree aria-hidden="true" className="h-8 w-8" />}
          title="No categories yet"
          description="Create the first category to start organising the catalogue."
          action={
            <Button type="button" onClick={() => setCreateOpen(true)}>
              <Plus aria-hidden="true" className="h-4 w-4" />
              New category
            </Button>
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((category, index) => {
            // Rendered only when the API supplies it — see Category.linkCount.
            const count = category.linkCount;
            return (
              <li key={category.categoryId}>
                <article className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 sm:flex-row sm:items-center">
                  <div className="flex shrink-0 flex-row gap-1 sm:flex-col">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={index === 0 || updateCategory.isPending}
                      onClick={() => move(index, -1)}
                      aria-label={`Move ${category.name} up`}
                    >
                      <ChevronUp aria-hidden="true" className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={index === items.length - 1 || updateCategory.isPending}
                      onClick={() => move(index, 1)}
                      aria-label={`Move ${category.name} down`}
                    >
                      <ChevronDown aria-hidden="true" className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-sm font-semibold">{category.name}</h2>
                      <Badge variant="outline" className="font-mono text-[11px]">
                        {category.slug}
                      </Badge>
                      {category.archived ? <Badge variant="muted">Archived</Badge> : null}
                      {count !== undefined ? (
                        <Badge variant="secondary">
                          {count} {pluralize(count, 'link')}
                        </Badge>
                      ) : null}
                    </div>
                    {category.description ? (
                      <p className="mt-1 text-sm text-muted-foreground">{category.description}</p>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(category)}
                    >
                      <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setMerging(category)}
                    >
                      <Merge aria-hidden="true" className="h-3.5 w-3.5" />
                      Merge
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => toggleArchived(category)}
                    >
                      {category.archived ? (
                        <ArchiveRestore aria-hidden="true" className="h-3.5 w-3.5" />
                      ) : (
                        <Archive aria-hidden="true" className="h-3.5 w-3.5" />
                      )}
                      {category.archived ? 'Restore' : 'Archive'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => setDeleting(category)}
                      aria-label={`Delete ${category.name}`}
                    >
                      <Trash2 aria-hidden="true" className="h-4 w-4" />
                    </Button>
                  </div>
                </article>
              </li>
            );
          })}
        </ul>
      )}

      <CategoryFormDialog open={createOpen} onOpenChange={setCreateOpen} />

      {editing ? (
        <CategoryFormDialog
          open
          onOpenChange={(open) => !open && setEditing(undefined)}
          category={editing}
        />
      ) : null}

      {merging ? (
        <MergeCategoryDialog
          open
          onOpenChange={(open) => !open && setMerging(undefined)}
          source={merging}
          categories={items}
        />
      ) : null}

      {deleting ? (
        <ConfirmDestructiveDialog
          open
          onOpenChange={(open) => !open && setDeleting(undefined)}
          title={`Delete “${deleting.name}”?`}
          description={
            <>
              The category is removed everywhere. Links that carry it keep every other category they
              have. If you want to keep the links grouped, merge instead.
            </>
          }
          confirmPhrase={deleting.slug}
          confirmLabel="Delete category"
          pending={deleteCategory.isPending}
          onConfirm={() =>
            deleteCategory.mutate(deleting.categoryId, {
              onSuccess: () => {
                toast({
                  title: 'Category deleted',
                  description: deleting.name,
                  variant: 'success',
                });
                setDeleting(undefined);
              },
              onError: (error) =>
                toast({
                  title: 'Could not delete the category',
                  description: describeError(error),
                  variant: 'error',
                }),
            })
          }
        />
      ) : null}
    </div>
  );
}
